import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCanvases } from '@/components/canvas/CanvasProvider';

export const route = { path: '/welcome', layout: 'owner', access: "profile === 'Learner_c'" };

export default function Welcome() {
  const navigate = useNavigate();
  const { loading, profile, needsWelcome, saveProfile } = useCanvases();
  const [name, setName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (loading || seeded.current) return;
    seeded.current = true;
    setName(/\s/.test(profile.name) ? profile.name : '');
    setOrganisation(profile.organisation || '');
  }, [loading, profile.name, profile.organisation]);

  const nameOk = name.trim().split(/\s+/).filter(Boolean).length >= 2;
  const orgOk = organisation.trim().length >= 2;

  async function submit(e) {
    e.preventDefault();
    setTouched(true);
    if (!nameOk || !orgOk) return;
    setSaving(true);
    try {
      const ok = await saveProfile({ name, organisation });
      if (!ok) {
        toast.error('Your details could not be saved. Please try again.');
        return;
      }
      toast.success('Welcome aboard. Your Case Card is next.');
      navigate('/dashboard');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-xl space-y-4" aria-busy="true">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-80 max-w-full" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      {!needsWelcome && (
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dashboard">
            <ApperIcon name="ArrowLeft" size={16} /> Back to Dashboard
          </Link>
        </Button>
      )}
      <div className="space-y-2">
        <span className="flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          <ApperIcon name={needsWelcome ? 'Sparkles' : 'UserRound'} size={24} />
        </span>
        <h1 className="font-heading text-3xl font-semibold text-balance">{needsWelcome ? 'Welcome to your canvases' : 'Your details'}</h1>
        <p className="text-muted-foreground">
          {needsWelcome
            ? 'Tell us who you are and where you work. It goes on the cover of your PDF report, and your facilitator sees it with your progress.'
            : 'These appear on the cover of your PDF report. Change them any time.'}
        </p>
      </div>
      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-5 shadow-xs md:p-6" noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="name">Your full name</Label>
          <Input
            id="name"
            autoComplete="name"
            placeholder="Priya Raman"
            value={name}
            aria-invalid={touched && !nameOk ? true : undefined}
            onChange={(e) => setName(e.target.value)}
          />
          {touched && !nameOk && <p className="text-xs font-medium text-destructive">Enter your first and last name.</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="organisation">Your organisation</Label>
          <Input
            id="organisation"
            autoComplete="organization"
            placeholder="Meridian Insurance"
            value={organisation}
            aria-invalid={touched && !orgOk ? true : undefined}
            onChange={(e) => setOrganisation(e.target.value)}
          />
          {touched && !orgOk && <p className="text-xs font-medium text-destructive">Enter the organisation you work for.</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" value={profile.email} disabled readOnly />
          <p className="text-xs text-muted-foreground">This is how you sign in. It cannot be changed here.</p>
        </div>
        <Button type="submit" size="lg" disabled={saving} className="w-full sm:w-auto">
          {saving ? (
            <>
              <ApperIcon name="LoaderCircle" size={16} className="animate-spin" /> Saving
            </>
          ) : needsWelcome ? (
            'Continue to my canvases'
          ) : (
            'Save details'
          )}
        </Button>
      </form>
    </div>
  );
}
